import React, { useCallback, useContext, useEffect, useState } from 'react';
import t from 'react-translate';
import { isEmpty, without } from 'underscore';
import { camelCase } from 'lodash';

import { AngularContext, AngularServicesContext } from 'react-app';
import { Button } from 'react-bootstrap';
import { IntegrationTypes } from 'redux/schemas/models/live-event';

import ZoomReAuthenticate from 'live_sessions/components/zoom-reauthenticate';
import SectionContent from '../section-content';
import { DisableProps } from './integrations';
import { config } from '@config/pendo.config.json';
import { config as appConfig } from '@config/config.json';

const ZOOM_OAUTH_DISABLE_ARTICLE_LINK = `${appConfig.helpLink}/article/ART-1754`;

type ZooomIntegrationProps = {
  onDisable?: (props: DisableProps) => void
};

const ZoomIntegration = (props: ZooomIntegrationProps) => {
  const { $uibModal, $scope } = useContext(AngularServicesContext);
  const { injectServices } = useContext(AngularContext);
  const [InstitutionsManager] = injectServices(['InstitutionsManager']);

  const [hasIntegration, setHasIntegration] = useState(false);
  const [integrationType, setIntegrationType] = useState<IntegrationTypes>();
  const [liveSessionSettings, setLiveSessionSettings] = useState(null);

  const integrationSettings = liveSessionSettings?.[camelCase(integrationType)];

  const checkIntegrationType = useCallback((type) => liveSessionSettings?.source?.includes(type), [liveSessionSettings?.source]);
  const zoomAccountLevel = checkIntegrationType(IntegrationTypes.ZOOM_ACCOUNT_LEVEL);
  const zoomUserLevel = checkIntegrationType(IntegrationTypes.ZOOM_USER_LEVEL);

  useEffect(() => {
    InstitutionsManager.getLiveSessionSettings().then((settings) => {
      setLiveSessionSettings(settings);
    });
  }, [InstitutionsManager]);

  useEffect(() => {
    $scope.$watch('vm.InstitutionsManager.institution.liveSessionSettings', (newValue) => {
      setLiveSessionSettings(newValue);
    });
  }, [$scope]);

  useEffect(() => {
    if (!liveSessionSettings) {
      return;
    }
    if (zoomAccountLevel) {
      setIntegrationType(IntegrationTypes.ZOOM_ACCOUNT_LEVEL);
    } else if (zoomUserLevel) {
      setIntegrationType(IntegrationTypes.ZOOM_USER_LEVEL);
    } else {
      setIntegrationType(null);
    }

    // Set hasIntegration based on if any integration type matches
    setHasIntegration(zoomAccountLevel || zoomUserLevel);
  }, [checkIntegrationType, liveSessionSettings, zoomAccountLevel, zoomUserLevel]);

  const configureZoom = () => {
    $uibModal.open({
      templateUrl: 'institutions/templates/configure-zoom-modal.html',
      controller: 'ConfigureZoomModalCtrl as vm',
      resolve: {
      },
    });
  };

  const disableZoom = () => {
    props.onDisable({
      action: () => InstitutionsManager.disableLiveSessionSettings(integrationType),
      onSuccess: () => {
        InstitutionsManager.institution.updateFromReact({
          liveSessionSettings: {
            apiKey: '',
            apiSecret: '',
            source: without(
              liveSessionSettings.source,
              integrationType,
            ),
          },
          zoomEnabled: false,
        });
        setHasIntegration(false);
      },
      successMessage: t.INSTITUTIONS.ADVANCED_SETTINGS.DISABLE_ZOOM_SUCCESS(),
      title: t.INSTITUTIONS.ADVANCED_SETTINGS.DISABLE_INTEGRATION(),
      bodyText: t.INSTITUTIONS.ADVANCED_SETTINGS.DISABLE_ZOOM_DESCRIPTION(),
      confirmDataQa: config.pendo.settings.zoom.disableConfirm,
      cancelDataQa: config.pendo.settings.zoom.disableCancel,
    });
  };

  const zoomDescription = zoomAccountLevel
    ? t.INSTITUTIONS.ADVANCED_SETTINGS.ZOOM_ACCOUNT_OAUTH_DESCRIPTION(ZOOM_OAUTH_DISABLE_ARTICLE_LINK)
    : t.INSTITUTIONS.ADVANCED_SETTINGS.ZOOM_INTEGRATION_DESCRIPTION();

  // Additional conditions to display CTA button for account auth
  const showConfigureCTA = !hasIntegration
    || (hasIntegration
      && zoomAccountLevel
      && (isEmpty(integrationSettings)
        || isEmpty(integrationSettings?.authenticatedStatus)
        || integrationSettings?.authenticatedStatus === 'not_configured'
      ));

  const showDisableCTA = hasIntegration
  && (zoomUserLevel
    || (integrationSettings?.authenticatedStatus === 'authenticated'
      || integrationSettings?.authenticatedStatus === 'not_authenticated'
    )
  );

  return (
    <SectionContent
      header={t.INSTITUTIONS.ADVANCED_SETTINGS.ZOOM_INTEGRATION_TITLE()}
      description={[zoomDescription]}
      showCTAButton={showConfigureCTA}
      ctaText={t.INSTITUTIONS.ADVANCED_SETTINGS.CONFIGURE()}
      onAction={configureZoom}
      buttonVariant={showConfigureCTA ? 'primary' : 'secondary'}
      dataQa={config.pendo.settings.zoom.cta}
      extras={(hasIntegration && (
        <div className='w-100'>
          {integrationSettings?.authenticatedStatus === 'not_authenticated'
          && zoomAccountLevel
          && (
            <ZoomReAuthenticate
              authenticatedUser={integrationSettings?.authenticatedUser}
              integrationType={integrationType}
            />
          )}
          {showDisableCTA && (
            <div className='button-bar mt-6'>
              <Button
                variant='secondary'
                onClick={disableZoom}
                data-qa={config.pendo.settings.zoom.cta}
              >
                {t.INSTITUTIONS.ADVANCED_SETTINGS.DISABLE_ZOOM()}
              </Button>
            </div>
          )}
        </div>
      ))}
    />
  );
};

export default ZoomIntegration;
